/**
 * Date utilities for consistent date formatting across the admin UI
 */

export type DateFormatStyle = 'short' | 'medium' | 'long' | 'full';

/**
 * Format a date value to a localized string
 * @param date - Date value (string, Date, or null/undefined)
 * @param locale - Locale string (default: browser locale)
 * @param style - Format style: 'short', 'medium', 'long', 'full'
 * @returns Formatted date string or '-' for invalid/empty dates
 */
export const formatDate = (
  date: string | Date | null | undefined,
  locale?: string,
  style: DateFormatStyle = 'medium'
): string => {
  if (!date) return '-';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check for invalid date
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    
    const options: Intl.DateTimeFormatOptions = (() => {
      switch (style) {
        case 'short':
          return { year: 'numeric', month: 'numeric', day: 'numeric' };
        case 'medium':
          return { year: 'numeric', month: 'short', day: 'numeric' };
        case 'long':
          return { year: 'numeric', month: 'long', day: 'numeric' };
        case 'full':
          return { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' };
        default:
          return { year: 'numeric', month: 'short', day: 'numeric' };
      }
    })();
    
    return dateObj.toLocaleDateString(locale, options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Format a date with time
 * @param date - Date value (string, Date, or null/undefined)
 * @param locale - Locale string (default: browser locale)
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted datetime string or '-' for invalid/empty dates
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  locale?: string,
  includeSeconds = false
): string => {
  if (!date) return '-';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' }),
    };
    
    return dateObj.toLocaleString(locale, options);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '-';
  }
};

/**
 * Format a date to ISO string format (YYYY-MM-DD) for input fields
 * @param date - Date value (string, Date, or null/undefined)
 * @returns ISO date string or empty string
 */
export const toInputDateFormat = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    return dateObj.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

/**
 * Check if a date is expired (before today)
 * @param date - Date value to check
 * @returns true if date is in the past
 */
export const isDateExpired = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;
  
  try {
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    // Create new Date instance to avoid mutating the input
    const normalizedDate = new Date(inputDate);
    normalizedDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return normalizedDate < today;
  } catch (error) {
    return false;
  }
};

/**
 * Check if a date is upcoming (within the next N days)
 * @param date - Date value to check
 * @param days - Number of days to consider as "upcoming"
 * @returns true if date is within the specified number of days
 */
export const isDateUpcoming = (
  date: string | Date | null | undefined,
  days = 7
): boolean => {
  if (!date) return false;
  
  try {
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    // Create new Date instance to avoid mutating the input
    const normalizedDate = new Date(inputDate);
    normalizedDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + days);
    
    return normalizedDate >= today && normalizedDate <= futureDate;
  } catch (error) {
    return false;
  }
};

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours")
 * @param date - Date value
 * @param locale - Locale string
 * @returns Relative time string
 */
export const getRelativeTime = (
  date: string | Date | null | undefined,
  locale?: string
): string => {
  if (!date) return '-';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    
    const now = new Date();
    const diffMs = dateObj.getTime() - now.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);
    
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    
    if (Math.abs(diffSec) < 60) {
      return rtf.format(diffSec, 'second');
    } else if (Math.abs(diffMin) < 60) {
      return rtf.format(diffMin, 'minute');
    } else if (Math.abs(diffHour) < 24) {
      return rtf.format(diffHour, 'hour');
    } else {
      return rtf.format(diffDay, 'day');
    }
  } catch (error) {
    return '-';
  }
};
